import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';

import type {
  ForecastReviewComparisonRow,
  ForecastReviewModifyPreview,
} from '@/services/forecastReviewService';
import type { ApproveAlertStep } from '../hooks/useApproveAlertProgressSteps';
import { ApproveAlertProgressSteps } from './ApproveAlertProgressSteps';
import { formatUiNumber } from '@/utils/uiLocale';

export type ModifyForecastSidebarPhase = 'confirm' | 'applying' | 'done' | 'error';

export interface ModifyForecastPeriodPayload {
  period: string;
  quantity: number;
}

interface ModifyForecastSidebarProps {
  open: boolean;
  row: ForecastReviewComparisonRow | null;
  preview: ForecastReviewModifyPreview | null;
  previewLoading: boolean;
  modalGranularity: 'week' | 'month';
  phase: ModifyForecastSidebarPhase;
  applySteps: ApproveAlertStep[];
  errorMessage: string | null;
  onModalGranularityChange: (granularity: 'week' | 'month') => void;
  onConfirm: (periods: ModifyForecastPeriodPayload[]) => void;
  onClose: () => void;
}

function formatQty(value: number, language?: string | null): string {
  return formatUiNumber(value, { maximumFractionDigits: 2 }, language);
}

function parseQtyInput(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function parsePctInput(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

export function ModifyForecastSidebar({
  open,
  row,
  preview,
  previewLoading,
  modalGranularity,
  phase,
  applySteps,
  errorMessage,
  onModalGranularityChange,
  onConfirm,
  onClose,
}: ModifyForecastSidebarProps) {
  const { t, i18n } = useTranslation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [visibleRow, setVisibleRow] = useState<ForecastReviewComparisonRow | null>(null);
  const [editMode, setEditMode] = useState<'absolute' | 'percent'>('absolute');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [periodPct, setPeriodPct] = useState<Record<string, string>>({});
  const [globalPct, setGlobalPct] = useState('');

  useEffect(() => {
    if (open && row) {
      setVisibleRow(row);
      setIsMounted(true);
      return;
    }
    if (!open && isMounted) {
      void sidebarRef.current?.offsetHeight;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(false);
          setTimeout(() => {
            setIsMounted(false);
            setVisibleRow(null);
          }, 300);
        });
      });
    }
  }, [open, row, isMounted]);

  useLayoutEffect(() => {
    if (isMounted && open && visibleRow) {
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isMounted, open, visibleRow]);

  useEffect(() => {
    if (!preview) return;
    const next: Record<string, number> = {};
    for (const period of preview.periods) {
      next[period.period] = period.forecast_quantity;
    }
    setQuantities(next);
    setPeriodPct({});
    setGlobalPct('');
    setEditMode('absolute');
  }, [preview]);

  const baselineByPeriod = useMemo(() => {
    const map: Record<string, number> = {};
    for (const period of preview?.periods ?? []) {
      map[period.period] = period.forecast_quantity;
    }
    return map;
  }, [preview]);

  const changedPeriods = useMemo((): ModifyForecastPeriodPayload[] => {
    if (!preview) return [];
    return preview.periods
      .filter((period) => quantities[period.period] !== period.forecast_quantity)
      .map((period) => ({
        period: period.period,
        quantity: quantities[period.period] ?? period.forecast_quantity,
      }));
  }, [preview, quantities]);

  const referenceTotals = useMemo(() => {
    if (!preview?.periods.length) {
      return { recentSalesTotal: 0, lastYearSalesTotal: 0 };
    }
    return preview.periods.reduce(
      (acc, period) => ({
        recentSalesTotal: acc.recentSalesTotal + period.recent_sales_quantity,
        lastYearSalesTotal: acc.lastYearSalesTotal + period.last_year_sales_quantity,
      }),
      { recentSalesTotal: 0, lastYearSalesTotal: 0 }
    );
  }, [preview]);

  const hasInvalidQty = useMemo(() => {
    if (!preview) return true;
    return preview.periods.some((period) => {
      const qty = quantities[period.period];
      return qty == null || !Number.isFinite(qty) || qty < 0;
    });
  }, [preview, quantities]);

  const isBusy = phase === 'applying' || previewLoading;
  const canClose = !isBusy;

  const handleQtyChange = (periodKey: string, raw: string) => {
    const parsed = parseQtyInput(raw);
    if (parsed == null) return;
    setQuantities((current) => ({ ...current, [periodKey]: parsed }));
    setPeriodPct((current) => {
      const next = { ...current };
      delete next[periodKey];
      return next;
    });
  };

  const handlePeriodPctChange = (periodKey: string, raw: string) => {
    setPeriodPct((current) => ({ ...current, [periodKey]: raw }));
  };

  const applyPctToPeriod = (periodKey: string, pct: number) => {
    const baseline = baselineByPeriod[periodKey] ?? 0;
    const nextQty = Math.round(baseline * (1 + pct / 100));
    setQuantities((current) => ({ ...current, [periodKey]: Math.max(0, nextQty) }));
  };

  const handleApplyPeriodPct = (periodKey: string) => {
    const pct = parsePctInput(periodPct[periodKey] ?? '');
    if (pct == null) return;
    applyPctToPeriod(periodKey, pct);
  };

  const handleApplyGlobalPct = () => {
    const pct = parsePctInput(globalPct);
    if (pct == null || !preview) return;
    const next: Record<string, number> = { ...quantities };
    const nextPct: Record<string, string> = {};
    for (const period of preview.periods) {
      const baseline = period.forecast_quantity;
      next[period.period] = Math.max(0, Math.round(baseline * (1 + pct / 100)));
      nextPct[period.period] = globalPct;
    }
    setQuantities(next);
    setPeriodPct(nextPct);
  };

  if (!isMounted || !visibleRow) return null;

  const subtitleParts = [
    visibleRow.element_code,
    visibleRow.family_name,
    visibleRow.supplier_name,
  ].filter(Boolean);

  const content = (
    <>
      <div
        className={`fixed inset-0 z-[90] bg-black transition-opacity duration-300 ease-in-out ${
          isAnimating ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={canClose ? onClose : undefined}
        aria-hidden
      />

      <div
        ref={sidebarRef}
        data-sidebar="modify-forecast"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modify-forecast-sidebar-title"
        className={`fixed inset-y-0 right-0 z-[100] flex w-full transform flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out md:w-1/2 ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 px-6 py-5">
          <div className="min-w-0 pr-4">
            <h2 id="modify-forecast-sidebar-title" className="text-2xl font-bold text-gray-900">
              {phase === 'done'
                ? t('forecastReview.modifyForecast.doneTitle')
                : visibleRow.element_name ?? visibleRow.element_code ?? `#${visibleRow.element_id}`}
            </h2>
            {subtitleParts.length > 0 ? (
              <p className="mt-1 text-sm text-gray-500">{subtitleParts.join(' · ')}</p>
            ) : null}
            <p className="mt-1 text-sm text-gray-600">
              <span className="text-gray-500">{t('forecastReview.comparison.aiModel')}:</span>{' '}
              <span className="font-medium text-gray-800">
                {visibleRow.ai_model_name ?? '—'}
              </span>
            </p>
            {preview ? (
              <p className="mt-1 text-xs text-gray-500">
                {t('forecastReview.modifyForecast.horizon', {
                  start: preview.horizon_start,
                  end: preview.horizon_end,
                })}
              </p>
            ) : null}
          </div>
          {canClose ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100"
              aria-label={t('forecastReview.modifyForecast.close')}
            >
              <X className="h-6 w-6" />
            </button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {phase === 'applying' ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{t('forecastReview.modifyForecast.progressHint')}</p>
              <ApproveAlertProgressSteps steps={applySteps} />
            </div>
          ) : phase === 'done' ? (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{t('forecastReview.modifyForecast.success')}</span>
            </div>
          ) : phase === 'error' ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMessage ?? t('forecastReview.modifyForecast.errorGeneric')}</span>
            </div>
          ) : (
            <>
              {preview && !previewLoading ? (
                <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">{t('forecastReview.modifyForecast.recentSalesKpi')}</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      {formatQty(referenceTotals.recentSalesTotal, i18n.language)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('forecastReview.modifyForecast.recentSalesKpiWindow', {
                        window: preview.reference_labels.recent_window,
                      })}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">{t('forecastReview.modifyForecast.lastYearSalesKpi')}</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      {formatQty(referenceTotals.lastYearSalesTotal, i18n.language)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('forecastReview.modifyForecast.lastYearSalesKpiWindow', {
                        window: preview.reference_labels.last_year_window,
                      })}
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
                  <button
                    type="button"
                    onClick={() => onModalGranularityChange('week')}
                    disabled={isBusy}
                    className={`px-2.5 py-1 text-xs font-medium ${modalGranularity === 'week' ? 'bg-[#1681ba] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t('forecastReview.modifyForecast.granularityWeek')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onModalGranularityChange('month')}
                    disabled={isBusy}
                    className={`px-2.5 py-1 text-xs font-medium ${modalGranularity === 'month' ? 'bg-[#1681ba] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t('forecastReview.modifyForecast.granularityMonth')}
                  </button>
                </div>
                <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
                  <button
                    type="button"
                    onClick={() => setEditMode('absolute')}
                    disabled={isBusy}
                    className={`px-2.5 py-1 text-xs font-medium ${editMode === 'absolute' ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t('forecastReview.modifyForecast.modeAbsolute')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditMode('percent')}
                    disabled={isBusy}
                    className={`px-2.5 py-1 text-xs font-medium ${editMode === 'percent' ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    {t('forecastReview.modifyForecast.modePercent')}
                  </button>
                </div>
              </div>

              {editMode === 'percent' ? (
                <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <label className="text-xs text-gray-600">
                    {t('forecastReview.modifyForecast.globalPct')}
                    <input
                      type="text"
                      inputMode="decimal"
                      value={globalPct}
                      onChange={(e) => setGlobalPct(e.target.value)}
                      className="ml-2 w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                      disabled={isBusy || !preview}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleApplyGlobalPct}
                    disabled={isBusy || !preview}
                    className="rounded border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    {t('forecastReview.modifyForecast.applyPct')}
                  </button>
                </div>
              ) : null}

              {previewLoading ? (
                <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('forecastReview.modifyForecast.loadingPreview')}
                </div>
              ) : preview ? (
                <>
                  <p className="mt-4 text-xs text-gray-500">
                    {t('forecastReview.modifyForecast.lastYearTableHint', {
                      window: preview.reference_labels.last_year_window,
                    })}
                  </p>
                  <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">
                            {t('forecastReview.modifyForecast.metric')}
                          </th>
                          {preview.periods.map((period) => (
                            <th
                              key={period.period}
                              className="min-w-[5.5rem] px-2 py-2 text-right font-medium text-gray-600"
                            >
                              {period.period}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-gray-700">
                            {t('forecastReview.modifyForecast.lastYearSales')}
                          </td>
                          {preview.periods.map((period) => (
                            <td key={period.period} className="px-2 py-2 text-right text-gray-600">
                              {formatQty(period.last_year_sales_quantity, i18n.language)}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="sticky left-0 z-10 bg-blue-50/60 px-3 py-2 font-medium text-gray-900">
                            {t('forecastReview.modifyForecast.yourForecast')}
                          </td>
                          {preview.periods.map((period) => {
                            const qty = quantities[period.period] ?? period.forecast_quantity;
                            const changed = qty !== period.forecast_quantity;
                            return (
                              <td key={period.period} className="px-2 py-2 text-right">
                                {editMode === 'absolute' ? (
                                  <input
                                    type="number"
                                    min={0}
                                    step="any"
                                    value={qty}
                                    onChange={(e) => handleQtyChange(period.period, e.target.value)}
                                    disabled={isBusy}
                                    className={`w-full rounded border px-1.5 py-1 text-right text-sm ${
                                      changed
                                        ? 'border-[#1681ba] bg-blue-50/50 font-medium text-gray-900'
                                        : 'border-gray-300 bg-white text-gray-800'
                                    }`}
                                  />
                                ) : (
                                  <div className="space-y-1">
                                    <div
                                      className={`text-sm ${changed ? 'font-medium text-[#1681ba]' : 'text-gray-800'}`}
                                    >
                                      {formatQty(qty, i18n.language)}
                                    </div>
                                    <div className="flex items-center justify-end gap-1">
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="%"
                                        value={periodPct[period.period] ?? ''}
                                        onChange={(e) =>
                                          handlePeriodPctChange(period.period, e.target.value)
                                        }
                                        disabled={isBusy}
                                        className="w-12 rounded border border-gray-300 px-1 py-0.5 text-right"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleApplyPeriodPct(period.period)}
                                        disabled={isBusy}
                                        className="rounded border border-gray-300 px-1 py-0.5 text-[10px] text-gray-600 hover:bg-gray-50"
                                      >
                                        {t('forecastReview.modifyForecast.apply')}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {changedPeriods.length > 0 ? (
                    <p className="mt-2 text-xs text-gray-500">
                      {t('forecastReview.modifyForecast.changedCount', {
                        count: changedPeriods.length,
                      })}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-amber-700">
                      {t('forecastReview.modifyForecast.noChanges')}
                    </p>
                  )}
                </>
              ) : errorMessage ? (
                <p className="mt-4 text-sm text-red-600">{errorMessage}</p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-6 py-4">
          {phase === 'confirm' ? (
            <>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onClose}
                disabled={isBusy}
              >
                {t('forecastReview.modifyForecast.cancel')}
              </button>
              <button
                type="button"
                className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                onClick={() => onConfirm(changedPeriods)}
                disabled={isBusy || !preview || changedPeriods.length === 0 || hasInvalidQty}
              >
                {t('forecastReview.modifyForecast.confirm')}
              </button>
            </>
          ) : phase === 'done' || phase === 'error' ? (
            <button
              type="button"
              className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              onClick={onClose}
            >
              {t('forecastReview.modifyForecast.close')}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}

/** @deprecated Use ModifyForecastSidebar */
export const ModifyForecastModal = ModifyForecastSidebar;
export type ModifyForecastModalPhase = ModifyForecastSidebarPhase;
